import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { AudioPanel } from '../../audio/ui/AudioPanel';
import { Credits } from '../../audio/ui/Credits';
import { useEscapeLayer } from '../useEscapeLayer';
import { ControlsTab } from './ControlsTab';
import { GraphicsTab } from './GraphicsTab';
import { InterfaceTab } from './InterfaceTab';

const TABS = [
  { id: 'controls', label: 'Controls', Panel: ControlsTab },
  { id: 'interface', label: 'Interface', Panel: InterfaceTab },
  { id: 'graphics', label: 'Graphics', Panel: GraphicsTab },
  { id: 'audio', label: 'Audio', Panel: AudioPanel },
] as const;

type TabId = (typeof TABS)[number]['id'];

/**
 * Options: every player setting in four tabs, applied live and saved by the persisted store (no
 * Save/Cancel). Not tied to Home, so the in-match HUD can open it later. ✕, Esc or a click
 * outside closes it; Credits opens on top with its own Esc layer.
 */
export function OptionsModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<TabId>('controls');
  const [credits, setCredits] = useState(false);
  const closeCredits = useCallback(() => setCredits(false), []);
  const tabRefs = useRef(new Map<TabId, HTMLButtonElement>());
  useEscapeLayer(onClose);

  // Focus starts on the active tab.
  useEffect(() => {
    tabRefs.current.get('controls')?.focus();
  }, []);

  const select = (id: TabId) => {
    setTab(id);
    tabRefs.current.get(id)?.focus();
  };
  // Arrow keys, Home and End move between tabs (WAI-ARIA tabs pattern).
  const onTabKey = (e: KeyboardEvent) => {
    const i = TABS.findIndex((t) => t.id === tab);
    const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: TABS.length - 1 }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    select(TABS[(next + TABS.length) % TABS.length]!.id);
  };

  const { Panel } = TABS.find((t) => t.id === tab)!;
  // Credits is a sibling, not a child: a click on its backdrop must not reach this one.
  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <section
          className="panel options"
          role="dialog"
          aria-modal="true"
          aria-labelledby="options-title"
          onClick={(e) => e.stopPropagation()}
        >
          <header>
            <h2 id="options-title">Options</h2>
            <button data-sound="back" aria-label="Close options" onClick={onClose}>
              ✕
            </button>
          </header>
          <div className="options-tabs" role="tablist" aria-label="Options" onKeyDown={onTabKey}>
            {TABS.map((t) => (
              <button
                key={t.id}
                ref={(el) => {
                  if (el) tabRefs.current.set(t.id, el);
                  else tabRefs.current.delete(t.id);
                }}
                id={`options-tab-${t.id}`}
                role="tab"
                aria-selected={t.id === tab}
                aria-controls="options-panel"
                tabIndex={t.id === tab ? 0 : -1}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div
            id="options-panel"
            className="options-panel"
            role="tabpanel"
            aria-labelledby={`options-tab-${tab}`}
          >
            <Panel />
          </div>
          <footer className="options-footer">
            <button className="link-button" onClick={() => setCredits(true)}>
              Credits
            </button>
          </footer>
        </section>
      </div>
      {credits && <Credits onClose={closeCredits} />}
    </>
  );
}
